import { isNumber } from 'lodash';
import { ChangeEvent, PropsWithChildren, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import InfoIcon from '@mui/icons-material/Info';
import FormHelperText from '@mui/material/FormHelperText';
import InputAdornment from '@mui/material/InputAdornment';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import { useHostingAccount } from '@newfold/huapi-js';
import { SitesInfo200ResourcesStorage } from '@newfold/huapi-js/src/index.schemas';

import Tooltip from '~/components/CommonTooltip';
import useAccount from '~/hooks/useAccount';
import { validateStorage } from '~/utils/cloud/validations';
import {
  calculateMaxStorageAllowed,
  getSiteStorageQuotaLimit,
} from '~/utils/hosting/helpers';

import { blockInvalidNumberChar } from '../utils/helpers';

interface StorageTypes {
  showLabel?: boolean;
  showStats?: boolean;
  defaultValue: number | undefined | null;
  siteId?: number | undefined;
  siteStorage?: SitesInfo200ResourcesStorage;
  isPending?: boolean;
  isStorageDisabled?: boolean;
  hideHelperText?: boolean;
  isStagingSite?: boolean;
}

const Storage = ({
  showLabel = false,
  showStats = false,
  defaultValue,
  siteId = undefined,
  siteStorage = undefined,
  isPending = false,
  isStorageDisabled = false,
  hideHelperText = false,
  isStagingSite = false,
  ...props
}: PropsWithChildren<StorageTypes>) => {
  const { t } = useTranslation('cloud');
  const { register, watch, setValue } = useFormContext();

  const storageRegisteredId = isNumber(siteId)
    ? `${siteId}-disk_limit_gib`
    : 'disk_limit_gib';
  const value = watch(storageRegisteredId, defaultValue);

  const { id: hostingId } = useAccount();
  const { data: hostingAccountData, isLoading: isHostingLoading } =
    useHostingAccount(hostingId, {
      query: {
        enabled: !!hostingId,
        select: ({ data }) => data,
      },
    });

  const { storage: accountStorage, staging } =
    hostingAccountData?.resources ?? {};
  const storage = siteStorage ?? accountStorage;

  const storageAvailable = isStagingSite
    ? staging?.storage?.available
    : accountStorage?.available;
  const siteStorageQuota = getSiteStorageQuotaLimit(accountStorage);
  const siteStorageUsed = siteStorage?.used ? siteStorage?.used : 0;
  const siteStorageTotal = siteStorage?.total;

  const minStorageAllowed = Math.max(siteStorageUsed, 1); // Minimum storage allowed is the greater of the used storage or 1
  const maxStorageAllowed = calculateMaxStorageAllowed({
    siteStorageTotal,
    storageAvailable,
    siteStorageQuota,
  });

  const [currAvailable, setCurrAvailable] = useState(
    maxStorageAllowed - Number(siteStorageTotal),
  );

  const { isValid, errorMessage } = validateStorage({
    value,
    min: minStorageAllowed,
    max: Number(maxStorageAllowed),
    siteStorageQuota,
    t,
  });

  const calculateStorage = (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const currSiteStorageTotal = event?.target?.value;
    setValue(storageRegisteredId, currSiteStorageTotal);

    if (showStats) {
      const remainingAvailable =
        Number(maxStorageAllowed) - Number(currSiteStorageTotal);
      const available =
        remainingAvailable >= 0 ? remainingAvailable : maxStorageAllowed;
      setCurrAvailable(Number(available));
    }
  };

  if (isPending || isHostingLoading)
    return <Skeleton width={260} height={56} />;

  return (
    <Stack spacing={0.5}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        // this should not always be centered (such as mobile design, add site flow)
        alignItems={showStats ? 'center' : undefined}
      >
        <TextField
          id={storageRegisteredId}
          type="number"
          {...(showLabel && {
            label: (
              <Stack direction="row" spacing={1} alignItems={'center'}>
                <Typography>
                  {t('hosting.overview.adjustStorage.storageLabel')}
                </Typography>
                {isStagingSite && (
                  <Tooltip
                    title={t('staging.storageTooltip')}
                    arrow
                    placement="top"
                    data-testid="storage-tooltip"
                  >
                    {/* @ts-ignore */}
                    <InfoIcon color="action" />
                  </Tooltip>
                )}
              </Stack>
            ),
          })}
          data-testid="storage-input"
          variant="outlined"
          value={value}
          error={!isValid}
          onKeyDown={blockInvalidNumberChar}
          disabled={isStorageDisabled}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">{storage?.unit}</InputAdornment>
            ),
          }}
          {...register(storageRegisteredId)}
          onChange={calculateStorage}
          {...props}
        />
        {showStats && (
          <div>
            <Typography color={'grey.700'}>
              {t('settings.adjustStorage.allocated')}
            </Typography>
          </div>
        )}
      </Stack>
      {!hideHelperText && !isValid && (
        <FormHelperText error>{errorMessage}</FormHelperText>
      )}
      {showStats && (
        <Stack direction={'row'} spacing={2} alignItems={'center'}>
          {isNumber(siteStorageUsed) && (
            <Typography variant="h4">
              {t('settings.adjustStorage.storageUsed', {
                used: siteStorageUsed,
                unit: storage?.unit,
              })}
            </Typography>
          )}
          {isNumber(currAvailable) && (
            <Typography variant="h4">
              {t('settings.adjustStorage.storageAvailable', {
                amount: isStagingSite ? storageAvailable : currAvailable,
                unit: storage?.unit,
              })}
            </Typography>
          )}
        </Stack>
      )}
    </Stack>
  );
};

export default Storage;
